/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isArray from 'lodash/isArray';

import MediumType from './medium-type';

/**
 * Záznam v registru informací o typech médií.
 * @template T
 * @typedef {Object} MediumRecord
 * @property {MediumType} mediumType Normalizovaný typ média na který se záznam vztahuje.
 * @property {T} value Hodnota záznamu registru.
 */

/**
 * Registr hodnot dle typů médií.
 * Slouží k výběru vhodného typu média a získání na něj navázaných hodnot.
 *
 * Pro porovnávání typů média dle priority se používá definice v [RFC 2616](https://tools.ietf.org/html/rfc2616#section-14).
 * Konkrétně zjednodušený algoritmus popsaný v sekci 14.
 *
 * Neuvažuje se suffix typu média, jelikož není definováno jak podle něj řadit (a vybrat vhodný).
 *
 * Dále se neuvažují parametry typu média. Např. `q` nebo `level` při řazení.
 * @template T
 */
class MediumRegistry {
  constructor() {
    /** Seznam registrovaných záznamů. */
    this.mediumRecordArray = /** @type {MediumRecord<T>[]} */ ([]);
  }

  /**
   * Nalezne nejlépe odpovídající záznam mezi registrovanými.
   * @param {string} mediumType Typ média pro který se mají nalézt informace.
   * @returns {T | null} Nalezený záznam v registru nebo null.
   */
  find(mediumType) {
    // Parsování typu média.
    const parsedMediumType = new MediumType(mediumType);
    if (parsedMediumType.type === '*' || parsedMediumType.subtype === '*') {
      throw new SyntaxError('Type range is not supported.');
    }
    const mediumRecordArray =
      // Vyhledání záznamů v registru a výběr vhodného z nalezených.
      this.mediumRecordArray
        .filter((mediumRecord) =>
          parsedMediumType.match(mediumRecord.mediumType)
        )
        .sort((aMediumRecord, bMediumRecord) =>
          MediumType.comparator(
            aMediumRecord.mediumType,
            bMediumRecord.mediumType
          )
        );
    /** @type {T | null} */
    let result;
    if (mediumRecordArray.length) {
      // Získání a vrácení informace o médiu z nejlépe odpovídajícímu záznamu.
      result = mediumRecordArray[0].value;
    } else {
      // Vrácení výchozí hodnoty, pokud nebylo nic nalezeno.
      result = null;
    }
    return result;
  }

  /**
   * Zjistí všechny zaregistrované záznamy.
   * @returns {T[]}
   */
  getAll() {
    return this.mediumRecordArray.map((mediumRecord) => mediumRecord.value);
  }

  /**
   * Zaregistruje záznam do registru.
   * @param {string | string[]} mediumType Typ média, nebo rozsah typů pro který se má záznam použít.
   * @param {T} value Nová hodnota k registraci do registru.
   */
  register(mediumType, value) {
    if (isArray(mediumType)) {
      this.registerMany(mediumType, value);
    } else {
      this.registerOne(mediumType, value);
    }
  }

  /**
   * Zaregistruje jeden datový typ pro jeden záznam.
   * @param {string} mediumType Typ média, nebo rozsah typů pro který se má záznam použít.
   * @param {T} value Nová hodnota k registraci do registru.
   * @private
   * @returns {MediumRecord<T>} Zaregistrovaný záznam.
   */
  registerOne(mediumType, value) {
    // Parsování typu média.
    const parsedMediumType = new MediumType(mediumType);
    if (Object.keys(parsedMediumType.parameters).length) {
      throw new SyntaxError('Parameters are not supported.');
    }
    // Kontrola, jestli již tento typ není registrovaný.
    // TODO #60 Šlo by optimalizovat při více položkách.
    // - Prohledat naráz.
    // - Nedovolit vložit nové položky dokud všechny neuspějí.
    const foundMediumRecord = this.mediumRecordArray.find((mediumRecord) =>
      parsedMediumType.equals(mediumRecord.mediumType)
    );
    if (foundMediumRecord) {
      throw new Error('Similar media type is already registered.');
    }
    // Vložení nového záznamu do registru.
    // TODO #60 Šlo by optimalizovat při více položkách.
    // - Vkládání naráz a ne po jedné položce.
    const mediumRecord = { mediumType: parsedMediumType, value };
    this.mediumRecordArray.push(mediumRecord);
    return mediumRecord;
  }

  /**
   * Zaregistruje více datových typů pro jeden záznam.
   * @param {Array.<string>} mediumTypeArray Typy médií, nebo rozsahy typů pro které se má záznam použít.
   * @param {T} value Nová hodnota k registraci do registru.
   * @private
   * @returns {MediumRecord<T>[]} Seznam zaregistrovaných záznamů.
   */
  registerMany(mediumTypeArray, value) {
    return mediumTypeArray.map((mediumType) =>
      this.registerOne(mediumType, value)
    );
  }
}

export default MediumRegistry;
